// /src/services/api.ts
import { API_BASE_URL } from "../config";
import { getToken } from "../lib/tokenStorage";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

async function baseRequest<T>(
  endpoint: string,
  method: HttpMethod,
  body?: unknown,
  init?: RequestInit,
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const token = getToken();
  const headers: Record<string, string> = {
    ...(body ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init?.headers as Record<string, string> | undefined),
  };

  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    ...init,
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");

  let data: any = null;
  try {
    const text = await res.text();
    if (text) {
      try {
        // Try to parse as JSON
        data = JSON.parse(text);
      } catch {
        // If parsing fails, try to extract JSON from text with PHP warnings/errors
        const jsonMatch = text.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            data = JSON.parse(jsonMatch[0]);
          } catch {
            data = text;
          }
        } else {
          data = text;
        }
      }
    }
  } catch {
    data = null;
  }

  if (!res.ok) {
    const message =
      data?.message ||
      (typeof data === "string" && data) ||
      `HTTP ${res.status}`;
    throw new Error(message);
  }

  return (data as T) ?? ({} as T);
}

export const api = {
  get: <T>(endpoint: string, init?: RequestInit) =>
    baseRequest<T>(endpoint, "GET", undefined, init),
  post: <T>(endpoint: string, body?: unknown, init?: RequestInit) =>
    baseRequest<T>(endpoint, "POST", body, init),
  put: <T>(endpoint: string, body?: unknown, init?: RequestInit) =>
    baseRequest<T>(endpoint, "PUT", body, init),
  patch: <T>(endpoint: string, body?: unknown, init?: RequestInit) =>
    baseRequest<T>(endpoint, "PATCH", body, init),
  delete: <T>(endpoint: string, init?: RequestInit) =>
    baseRequest<T>(endpoint, "DELETE", undefined, init),
};
